import { LifeBuoy } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { IntegrationService } from '@axilon-apps/domains-ontology-ui-state'

/**
 * Static, per-service descriptor that drives the Integrations tab UI:
 * the picker card, the add/edit form's field labels/placeholders, and
 * how an integration row is rendered.
 *
 * This registry is the single extension point for new external data
 * providers. To add Slack / Jira / GitHub later:
 *   1. add the service string to `IntegrationService` (state `types.ts`)
 *      and to the proxy's accepted-services list, then
 *   2. append a descriptor here.
 * Everything else (picker, form, list rows, create/update orchestration)
 * is data-driven off this array and needs no further change.
 */
export interface IntegrationProvider {
  service: IntegrationService
  /** Display name shown in the picker, list rows, and dialog title. */
  label: string
  /** One-line description shown under the label in the picker. */
  description: string
  Icon: LucideIcon
  /** Label for the per-instance identifier field (e.g. the ServiceNow instance). */
  instanceLabel: string
  instancePlaceholder: string
  instanceHelp?: string
  /** Label for the write-only secret credential field. */
  apiKeyLabel: string
  apiKeyPlaceholder: string
  apiKeyHelp?: string
  /**
   * Whether the provider requires a credential on creation. Today every
   * provider does, but keeping the flag makes future no-auth / OAuth
   * providers expressible without reworking the form.
   */
  requiresApiKey: boolean
}

export const INTEGRATION_PROVIDERS: readonly IntegrationProvider[] = [
  {
    service: 'servicenow',
    label: 'ServiceNow',
    description: 'Sync incidents and work orders from a ServiceNow instance.',
    Icon: LifeBuoy,
    instanceLabel: 'Instance name',
    instancePlaceholder: 'my-prod-instance',
    instanceHelp: 'Your ServiceNow instance identifier (e.g. the subdomain of your instance URL).',
    apiKeyLabel: 'API key',
    apiKeyPlaceholder: 'Paste the ServiceNow API key',
    apiKeyHelp: 'Stored encrypted. The key is write-only and is never displayed after saving.',
    requiresApiKey: true
  }
] as const

export function getIntegrationProvider(
  service: IntegrationService
): IntegrationProvider | undefined {
  return INTEGRATION_PROVIDERS.find((p) => p.service === service)
}
